import { useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { Box, Button, Container, CssBaseline, Stack, ThemeProvider, Typography } from '@mui/material';

import { paths } from '@/paths';
import { getCustomDomainHostname } from '@/lib/custom-domain';
import { getCanonicalOrigin, getPublicUrl } from '@/lib/seo/canonical-origin';
import { resolveSeoMetadata } from '@/lib/seo/metadata';
import { buildEventStructuredData, buildStructuredData, type JsonLdDocument } from '@/lib/seo/structured-data';
import { usePublicSiteSeo } from '@/hooks/use-public-site-seo';
import { usePublicSite, type PublicSiteIdentifier } from '@/hooks/use-public-site';
import { useUpcomingEvents } from '@/hooks/use-public-site-modules';
import { useAccessibilityPreferences } from '@/hooks/use-accessibility-preferences';
import { useEffectiveWebsiteTheme } from '@/hooks/use-effective-website-theme';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { resolveButtonFontKey, useGoogleFonts } from '@/styles/theme/create-unit-site-theme';
import { DEFAULT_THEME_CONFIG } from '@/lib/site-styles/theme-presets';
import { SiteOrgProvider } from '@/lib/site-org-context';
import { AccessibilityPreferencesStateProvider } from '@/lib/accessibility/accessibility-preferences-context';
import { SitePageChrome } from '@/components/public-site/SitePageChrome';
import { SiteComingSoon } from '@/components/public-site/SiteComingSoon';
import { AccessibilityPreferencesWidget } from '@/components/public-site/accessibility/AccessibilityPreferencesWidget';
import { GlobalMotionStyles } from '@/components/public-site/accessibility/GlobalMotionStyles';

export default function PublicSite() {
  const { slug, pageSlug: routePageSlug } = useParams<{ slug: string; pageSlug?: string }>();
  const location = useLocation();

  // A verified custom domain overrides the default /site/:slug routing —
  // when present, the whole path (not just a :pageSlug route param) is
  // this one unit's site, so the page slug comes from the path itself.
  const customDomainHostname = getCustomDomainHostname();
  const identifier: PublicSiteIdentifier | null = customDomainHostname
    ? { type: 'domain', hostname: customDomainHostname }
    : slug
      ? { type: 'slug', slug }
      : null;
  const pageSlug = customDomainHostname ? location.pathname.replace(/^\/+/, '') || undefined : routePageSlug;
  const basePath = customDomainHostname ? '' : `/site/${slug}`;

  const { data, isLoading } = usePublicSite(identifier, pageSlug);
  const isPreview = new URLSearchParams(location.search).get('preview') === 'true';

  const origin = data ? getCanonicalOrigin(data.slug, data.activeDomainHostname) : null;
  const canonicalPath = data?.currentPage && !data.currentPage.is_home ? data.currentPage.slug : '';
  const seoMetadata =
    data && origin
      ? resolveSeoMetadata({ site: data.site, page: data.currentPage, unit: data.unit, origin, pathname: canonicalPath })
      : null;
  const isIndexable = Boolean(data && origin && seoMetadata && seoMetadata.robots === 'index, follow');

  // Event schema only for a page that actually shows the real calendar
  // (the block itself gates on the same calendar_visible_on_site switch —
  // see CalendarBlockView) — fetching is skipped entirely otherwise via
  // useUpcomingEvents' own enabled-on-orgId gate.
  const hasCalendarBlock = data?.blocks.some((block) => block.block_type === 'calendar') ?? false;
  const shouldFetchEvents = isIndexable && hasCalendarBlock && Boolean(data?.site.calendar_visible_on_site);
  const { data: upcomingEvents } = useUpcomingEvents(shouldFetchEvents ? (data?.site.clerk_org_id ?? null) : null);

  const structuredData: JsonLdDocument[] =
    data && origin && seoMetadata && isIndexable
      ? [
          ...buildStructuredData({ site: data.site, unit: data.unit, origin, metadata: seoMetadata }),
          ...(upcomingEvents ? buildEventStructuredData(upcomingEvents, `${getPublicUrl(origin)}#organization`) : []),
        ]
      : [];
  usePublicSiteSeo(seoMetadata, structuredData);

  // A client-side stand-in for a real 301: once a unit's custom domain is
  // fully active, the platform-path URL is no longer canonical (see
  // getCanonicalOrigin) and should hand visitors — and crawlers that
  // execute JS — off to the real address. This app has no server, so it
  // can't send an actual 301 status; a host-aware redirect at the proxy
  // layer that serves sitemap.xml/robots.txt (see server.js) is the
  // follow-up for crawlers that don't execute JS.
  useEffect(() => {
    if (identifier?.type === 'slug' && data?.activeDomainHostname && seoMetadata) {
      window.location.replace(seoMetadata.canonicalUrl + location.search);
    }
  }, [identifier?.type, data?.activeDomainHostname, seoMetadata, location.search]);

  // This ThemeProvider (+ its own CssBaseline) is the ONLY theme this
  // route ever renders in — theme-provider.tsx's isPublicSiteRoute() skips
  // the platform's own CssVarsProvider/CssBaseline entirely for /site/*
  // and custom-domain requests, so nothing here can inherit or leak the
  // Scouting Ops platform theme. Falls back to the same defaults
  // createUnitSiteTheme uses for an unconfigured site while loading/not
  // found, rather than going themeless (which would fall through to MUI's
  // own stock default theme and skip a baseline reset).
  const themeConfig = data?.site.theme_config ?? DEFAULT_THEME_CONFIG;
  useGoogleFonts([themeConfig.fonts.headingFont, themeConfig.fonts.paragraphFont, resolveButtonFontKey(themeConfig.fonts), themeConfig.fonts.miscFont]);

  // Preferences are visitor-scoped by the real sites.id UUID once it's
  // known; before that (still loading), a stable identifier-derived key is
  // used instead — use-accessibility-preferences.ts reloads from storage
  // whenever this key changes, so it picks up the visitor's real saved
  // preferences the moment `data` resolves rather than being stuck with
  // whatever (almost certainly nothing) loaded under the temporary key.
  // One hook call feeds both the context (for the panel) and the theme
  // below, so the two can never drift out of sync with each other.
  const preferencesStorageKey = data?.site.id ?? customDomainHostname ?? slug ?? 'unknown-site';
  const accessibility = useAccessibilityPreferences(preferencesStorageKey);
  const osReducedMotion = useReducedMotion();
  const effectivePreferences = useMemo(
    () => ({ ...accessibility.preferences, reduceMotion: accessibility.preferences.reduceMotion || osReducedMotion }),
    [accessibility.preferences, osReducedMotion]
  );
  const theme = useEffectiveWebsiteTheme(themeConfig, effectivePreferences);

  return (
    <AccessibilityPreferencesStateProvider value={accessibility}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalMotionStyles />
        {isLoading ? (
          <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        ) : !data ? (
          <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
            <Stack spacing={2} sx={{ alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Site Not Found</Typography>
              <Typography color="text.secondary">This Scout unit site doesn&apos;t exist or isn&apos;t published yet.</Typography>
              {customDomainHostname ? null : (
                <Button component={Link} to="/" variant="contained">Go Home</Button>
              )}
            </Stack>
          </Box>
        ) : (
          <SiteOrgProvider orgId={data.site.clerk_org_id}>
            {isPreview ? (
              <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', py: 1 }}>
                <Container maxWidth="lg">
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Preview Mode</Typography>
                    <Button component={Link} to={paths.studio} size="small" variant="text" sx={{ color: 'inherit' }}>
                      Back to Editor
                    </Button>
                  </Stack>
                </Container>
              </Box>
            ) : null}

            {data.pages.length === 0 ? (
              <SiteComingSoon unit={data.unit} logoUrl={data.site.logo_url} />
            ) : (
              <SitePageChrome
                basePath={basePath}
                blocks={data.blocks}
                currentPage={data.currentPage}
                logoUrl={data.site.logo_url}
                pages={data.pages.filter((page) => page.is_published)}
                unit={data.unit}
              />
            )}

            <AccessibilityPreferencesWidget enabled={data.site.accessibility_launcher_enabled} />
          </SiteOrgProvider>
        )}
      </ThemeProvider>
    </AccessibilityPreferencesStateProvider>
  );
}
